import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { Storey } from '@shared/models/design/storey.model';
import {
  createBlueprintUnderlay,
  getBlueprintDrawingScaleRatio,
  getDefaultBlueprintStoreyAssignment,
} from '@shared/helpers/design/blueprint-underlay.helpers';
import { inheritBlueprintCalibrationFromSource } from '@shared/helpers/design/blueprint-calibration.helpers';
import {
  loadPdfDocument,
  rasterizePdfPageFromDoc,
} from './pdf-rasterize.helpers';

export type SilentBlueprintPlaceResult =
  { kind: 'placed'; underlays: BlueprintUnderlay[] } | { kind: 'needsWizard' };

type TryBuildSilentBlueprintUnderlaysOptions = {
  file: File;
  /** Calibrated page to inherit scale from, when one exists. */
  matchScaleFrom: BlueprintUnderlay | undefined;
  selectedStoreyLevel?: number;
  storeys: readonly Storey[];
};

/**
 * Place a single-page PDF without the import wizard.
 * Assigns the Design-selected storey. Inherits scale when
 * {@link TryBuildSilentBlueprintUnderlaysOptions.matchScaleFrom} is calibrated.
 * Multi-page PDFs still need Select pages.
 */
export const tryBuildSilentBlueprintUnderlays = async ({
  file,
  matchScaleFrom,
  selectedStoreyLevel,
  storeys,
}: TryBuildSilentBlueprintUnderlaysOptions): Promise<SilentBlueprintPlaceResult> => {
  const loaded = await loadPdfDocument(file);
  try {
    if (loaded.pageCount !== 1) return { kind: 'needsWizard' };

    const pageIndex = 0;
    const raster = await rasterizePdfPageFromDoc(loaded.doc, pageIndex);
    const assignment = getDefaultBlueprintStoreyAssignment({
      underlays: [],
      selectedStoreyLevel,
      storeys,
    });
    const inheritFrom =
      matchScaleFrom?.calibration?.primary !== undefined
        ? matchScaleFrom
        : undefined;
    const created = createBlueprintUnderlay({
      imageDataUrl: raster.imageDataUrl,
      pageIndex: raster.pageIndex,
      fileName: file.name,
      label: loaded.pageNames[pageIndex],
      pixelWidth: raster.pixelWidth,
      pixelHeight: raster.pixelHeight,
      paperWidthMm: raster.paperWidthMm,
      paperHeightMm: raster.paperHeightMm,
      drawingScaleRatio: inheritFrom
        ? getBlueprintDrawingScaleRatio(inheritFrom)
        : undefined,
      storeyAssignment: assignment,
    });

    const underlay = inheritFrom
      ? inheritBlueprintCalibrationFromSource(created, inheritFrom)
      : created;

    return { kind: 'placed', underlays: [underlay] };
  } finally {
    await loaded.destroy();
  }
};
